import type { TimelineRow } from '@tabel/shared/agent-events/timeline';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { type ReactNode, memo, useId } from 'react';
import { turnSteps } from '../../../lib/agent-api';
import { errorSentence } from '../../ErrorToast';
import { Skeleton } from '../../ui';
import { LABEL, T, say } from '../texts';
import { stepsKey } from '../useThread';

type Row = Extract<TimelineRow, { kind: 'turn' }>;

/**
 * Свёрнутая работа агента (contracts/ui.md §2; FR-020): кнопка «Работал 2 мин · 8 шагов ›». При раскрытии шаги
 * догружаются (`GET …/turns/:runId/steps`), над ними — «Расход ответа: N тыс. токенов». Раскрытие помнит лента.
 */
export const TurnRow = memo(function TurnRow({
  row,
  threadId,
  expanded,
  onToggle,
  renderRow,
}: {
  row: Row;
  threadId: string;
  expanded: boolean;
  onToggle: (key: string) => void;
  /** Строка из раскрытой работы — тем же видом, что в ленте. */
  renderRow: (row: TimelineRow) => ReactNode;
}) {
  const id = useId();
  const runId = row.runId ?? '';
  const steps = useQuery({
    queryKey: stepsKey(threadId, runId),
    queryFn: () => turnSteps(threadId, runId),
    enabled: expanded && runId !== '',
    staleTime: Number.POSITIVE_INFINITY,
  });
  const label =
    row.outcome === 'cancelled' ? say.stoppedTurn(row.steps) : say.worked(row.workedMs, row.steps);
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={id}
        onClick={() => onToggle(row.key)}
        className="-ml-2 inline-flex h-7 items-center gap-1.5 self-start rounded-control px-2 text-[12.5px] font-medium text-ink-2 transition-colors hover:bg-subtle hover:text-ink max-tablet:min-h-10"
      >
        <ChevronRight
          size={16}
          strokeWidth={1.75}
          aria-hidden
          className={`transition-transform duration-150 motion-reduce:transition-none ${expanded ? 'rotate-90' : ''}`}
        />
        {label}
      </button>
      {expanded && (
        <div id={id} className="flex flex-col gap-0.5">
          {row.totalTokens !== null && (
            <p className="-mt-1 mb-1 text-[12px] text-ink-2">{say.tokens(row.totalTokens)}</p>
          )}
          {steps.isLoading && (
            <div aria-busy="true" aria-label={LABEL.loading} className="space-y-2 py-1">
              <Skeleton className="h-3.5 w-3/5" />
              <Skeleton className="h-3.5 w-2/5" />
            </div>
          )}
          {steps.isError && (
            <p role="alert" className="text-[12.5px] text-bad">
              {T.stepsFailed} {errorSentence(steps.error)}{' '}
              <button
                type="button"
                onClick={() => void steps.refetch()}
                className="font-medium text-accent hover:underline"
              >
                {LABEL.retry}
              </button>
            </p>
          )}
          {steps.data && steps.data.length === 0 && <p className="text-[12.5px] text-ink-2">{T.noSteps}</p>}
          {steps.data?.map((r, i) => (
            <div key={r.key} className={i > 0 && r.kind !== 'step' ? 'pt-2' : undefined}>
              {renderRow(r)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});
